import {
  ActivityType,
  Gym,
  GymEnvironment,
  GymTypes,
  IGroupTemplate,
  ILeaderboardConfig,
  ILeaderboardDataViewModel,
  ILeaderboardInstance,
  ILeagueConfig,
  ITeamConfig,
  LeaderboardMetric,
  leaderboardSource,
  Organisation,
} from 'fitbeat.models';

import { generateUniqueId } from 'fitbeat.gym-core';

import moment from 'moment';

export function mapLeaderboardInstanceToOption(
  leaderboardInstance: ILeaderboardInstance,
  timezone?: string,
) {
  const getMomentDate = (date: string) => timezone
    ? moment.tz(date, timezone)
    : moment(date);

  const startDateMoment = getMomentDate(leaderboardInstance.startDate);

  const endDateMoment = leaderboardInstance.endDate !== 'undefined'
    ? getMomentDate(leaderboardInstance.endDate)
    : null;

  return {
    value: leaderboardInstance,
    label: `${startDateMoment.format('D MMM YYYY')} - ${endDateMoment
      ? endDateMoment.format('D MMM YYYY')
      : 'Recurring'}`,
  };
}

export function mapLeaderboardTeamsToOption(
  teamName: string,
) {
  return {
    value: teamName,
    label: teamName,
  };
}

export function mapTeamsLeaderboardAverageMinutesToSeconds(
  leaderboardInstanceDetails: ILeaderboardDataViewModel,
): ILeaderboardDataViewModel {
  const updatedRankings = leaderboardInstanceDetails?.rankings.map((leaderboard) => ({
    ...leaderboard,
    value: leaderboard.value * 60,
  }));

  const teamInstanceDetails = {
    ...leaderboardInstanceDetails,
    rankings: updatedRankings,
  };
  return teamInstanceDetails;
}

export function setLeaderboardConfig(
  gyms: Gym[],
  selectedGymName: string,
  isOrgAdmin: boolean,
  allGymsInOrg: string,
) {
  const timezones = moment.tz.names();

  const orderedGyms = [...gyms].sort((a, b) => {
    if (a.name === selectedGymName) {
      return -1;
    }
    if (b.name === selectedGymName) {
      return 1;
    }
    return 0;
  });

  if (isOrgAdmin) {
    orderedGyms.unshift({
      name: allGymsInOrg,
      displayName: 'All Gyms',
      created: new Date(),
      type: GymTypes.Indoor,
      numOfZones: 0,
      timezone: 'Australia/Sydney',
      iso2Country: '',
      zoneMapping: [],
      publicHolidays: [],
      cancelledDays: [],
      environment: GymEnvironment.None,
      allowedOnProductIds: [],
      autoCheckin: false,
      enableStudioAppCheckIn: true,
      allowedCapacityAsPercentOfFullGym: 1,
      enabled: true,
      accountId: '',
      websiteSlug: '',
      singleStationPerZoneGym: false,
      shouldUseInAppPurchaseForMembership: false,
      organisation: Organisation.Test,
      specificTimeSlotTypes: [],
    });
  }
  return { orderedGyms, timezones };
}

export function isExperiencePointsMetric(
  metricOrConfig: LeaderboardMetric | Partial<ILeaderboardConfig>,
): boolean {
  const metric = typeof metricOrConfig === 'string'
    ? metricOrConfig
    : metricOrConfig.metric;
  return !!metric && leaderboardSource(metric as LeaderboardMetric) === 'experience-points';
}

export function isTeamParticipationBonusEligible(leaderboardConfig: ILeaderboardConfig): boolean {
  return leaderboardConfig.competitionType === 'inter-team'
    && leaderboardConfig.metric === 'team-weighted-xp-score';
}

export function transformLeaderboardConfig(
  formData: ILeaderboardConfig,
  allGymsName: string,
  teams: ITeamConfig[],
  updating: boolean,
  prizesAndNotes: string[],
  restrictedToActivityTypes: ActivityType[],
  leagueConfigs: ILeagueConfig[],
  enableLeagues: boolean,
) {
  if (!updating) {
    const allGymsInOrg = formData.gymName === allGymsName;
    formData.startDate = moment.tz(formData.startDate, formData.timezone).startOf('day').toDate();

    if (allGymsInOrg) {
      formData.gymName = undefined;
    }
  }
  const normalizedTeams = teams.map((team, index) => ({
    ...team,
    id: team.id || generateUniqueId(team.name, teams, index),
  }));
  const teamNames = normalizedTeams.map((team) => team.name);
  if (formData.competitionType === 'inter-team') {
    formData.teamConfigs = normalizedTeams;
    formData.teams = teamNames;
    formData.leagueConfig = enableLeagues ? leagueConfigs : undefined;
  } else {
    formData.teamConfigs = undefined;
    formData.teams = undefined;
    formData.leagueConfig = undefined;
  }
  if (!isTeamParticipationBonusEligible(formData)) {
    formData.dailyTeamParticipationBonusMaxXp = undefined;
  }
  formData.prizesAndNotes = prizesAndNotes.length > 0
    ? prizesAndNotes
    : undefined;
  formData.restrictedToActivityTypes = !!restrictedToActivityTypes && restrictedToActivityTypes.length > 0
    ? restrictedToActivityTypes
    : undefined;
  if (formData.endDate) {
    formData.endDate = moment.tz(formData.endDate, formData.timezone).endOf('day').toDate();
  }
}

const leaderboardFieldsOverriddenByInterestGroup = [
  'requireOptIn',
  'showInStudio',
  'competitionType',
  'metric',
  'frequency',
  'teams',
  'teamConfigs',
  'leagueConfig',
  'restrictedToActivityTypes',
  'dailyTeamParticipationBonusMaxXp',
  'capMinutes',
  'includeImportedWorkouts',
  'prizesAndNotes',
] as const;

export type LeaderboardFieldsOverriddenByInterestGroup =
  Pick<ILeaderboardConfig, typeof leaderboardFieldsOverriddenByInterestGroup[number]>;

export function captureLeaderboardFieldsOverriddenByInterestGroup(
  formData: ILeaderboardConfig,
): LeaderboardFieldsOverriddenByInterestGroup {
  return leaderboardFieldsOverriddenByInterestGroup.reduce(
    (snapshot, field) => ({ ...snapshot, [field]: formData[field] }),
    {} as LeaderboardFieldsOverriddenByInterestGroup,
  );
}

export function applyInterestGroupInvariants(formData: ILeaderboardConfig): void {
  formData.requireOptIn = true;
  formData.showInStudio = false;
  formData.competitionType = 'individual';
  formData.metric = 'experience-points';
  formData.frequency = 'weekly';
  formData.teams = undefined;
  formData.teamConfigs = undefined;
  formData.leagueConfig = undefined;
  formData.restrictedToActivityTypes = undefined;
  formData.dailyTeamParticipationBonusMaxXp = undefined;
  formData.capMinutes = undefined;
  formData.includeImportedWorkouts = undefined;
  formData.prizesAndNotes = undefined;
}

export function clearInterestGroupFields(
  formData: ILeaderboardConfig,
  restoredLeaderboardFields?: LeaderboardFieldsOverriddenByInterestGroup,
): void {
  formData.socialInterest = undefined;
  if (restoredLeaderboardFields) {
    Object.assign(formData, restoredLeaderboardFields);
  }
}

export function isTemplateAlreadyCreated(
  template: IGroupTemplate,
  configs: ILeaderboardConfig[],
  gymName: string,
): boolean {
  return configs.some((config) => config.name === template.name
    && (!config.gymName || config.gymName === gymName));
}
